import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';

const Resignation = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    lastWorkingDay: '',
    reason: '',
    additionalComments: '',
    acknowledgment: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Here you would typically send the data to your backend
      console.log('Resignation submitted:', formData);
      
      setSubmitStatus({
        type: 'success',
        message: t('resignation.success')
      });
      
      // Reset form
      setFormData({
        lastWorkingDay: '',
        reason: '',
        additionalComments: '',
        acknowledgment: false
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: t('resignation.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          {t('resignation.title')}
        </h1>

        {submitStatus.message && (
          <div className={`mb-6 p-4 rounded-md ${
            submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {submitStatus.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="lastWorkingDay" className="block text-sm font-medium text-gray-700">
              {t('resignation.lastWorkingDay')}
            </label>
            <input
              type="date"
              id="lastWorkingDay"
              name="lastWorkingDay"
              required
              min={new Date().toISOString().split('T')[0]}
              value={formData.lastWorkingDay}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
              {t('resignation.reason.label')}
            </label>
            <select
              id="reason"
              name="reason"
              required
              value={formData.reason}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">{t('resignation.reason.placeholder')}</option>
              <option value="new_opportunity">{t('resignation.reason.options.newOpportunity')}</option>
              <option value="personal">{t('resignation.reason.options.personal')}</option>
              <option value="relocation">{t('resignation.reason.options.relocation')}</option>
              <option value="retirement">{t('resignation.reason.options.retirement')}</option>
              <option value="other">{t('resignation.reason.options.other')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="additionalComments" className="block text-sm font-medium text-gray-700">
              {t('resignation.additionalComments.label')}
            </label>
            <textarea
              id="additionalComments"
              name="additionalComments"
              rows={4}
              value={formData.additionalComments}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder={t('resignation.additionalComments.placeholder')}
            />
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="acknowledgment"
                name="acknowledgment"
                type="checkbox"
                required
                checked={formData.acknowledgment}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="acknowledgment" className="font-medium text-gray-700">
                {t('resignation.acknowledgment.label')}
              </label>
              <p className="text-gray-500">
                {t('resignation.acknowledgment.description')}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting || !formData.acknowledgment}
              className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm
                ${isSubmitting || !formData.acknowledgment
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                }`}
            >
              {isSubmitting ? t('resignation.submitting') : t('resignation.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Resignation;
